import { useState, useEffect, useCallback } from 'react';
import { ShieldCheck } from 'lucide-react';
import {
  listPendingApprovals,
  approveMarketplaceListing,
  rejectMarketplaceListing,
  ApiError,
} from '../api/client.js';
import type { PublishedCatalog } from '../api/client.js';

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function MarketplaceAdminPage() {
  const [pending, setPending] = useState<PublishedCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectingItem, setRejectingItem] = useState<PublishedCatalog | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const loadPending = useCallback(async () => {
    try {
      const data = await listPendingApprovals();
      setPending(data.pending);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handleApprove = async (id: string) => {
    setActionId(id);
    try {
      await approveMarketplaceListing(id);
      showToast('Catalog approved and listed on marketplace');
      await loadPending();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to approve');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectingItem) return;
    setRejectLoading(true);
    try {
      await rejectMarketplaceListing(rejectingItem.id, rejectReason || 'No reason provided');
      showToast('Catalog listing rejected');
      setRejectingItem(null);
      setRejectReason('');
      await loadPending();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to reject');
    } finally {
      setRejectLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Marketplace Admin</h1>
        <p className="mt-1 text-sm text-gray-400">
          Review and approve marketplace listing requests
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {toast && (
        <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
          {toast}
        </div>
      )}

      {pending.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-300">No pending requests</h3>
          <p className="mt-2 text-sm text-gray-500">
            All marketplace listing requests have been reviewed.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-800 bg-gray-900">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3">Publisher</th>
                <th className="px-5 py-3">Catalog</th>
                <th className="px-5 py-3 text-center">Products</th>
                <th className="px-5 py-3">Requested</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((item) => (
                <tr key={item.id} className="border-t border-gray-800">
                  <td className="px-5 py-3">
                    <div className="text-sm font-medium text-gray-200">{item.publisherName}</div>
                    <div className="text-xs text-gray-500">{item.tenantName}</div>
                    {(item.ownerName || item.ownerEmail) && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        {item.ownerName}{item.ownerName && item.ownerEmail ? ' · ' : ''}{item.ownerEmail}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-sm text-gray-200">{item.catalogName}</div>
                    {item.catalogDescription && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                        {item.catalogDescription}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-center text-sm text-gray-400">
                    {item.productCount}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">
                    {relativeTime(item.marketplaceRequestedAt)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleApprove(item.id)}
                        disabled={actionId === item.id}
                        className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {actionId === item.id ? '...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => {
                          setRejectingItem(item);
                          setRejectReason('');
                        }}
                        className="rounded-lg bg-red-600/20 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-600/30"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Reject Listing</h2>
            <p className="mb-4 text-sm text-gray-400">
              Reject &ldquo;{rejectingItem.catalogName}&rdquo; from {rejectingItem.publisherName}?
            </p>
            <div className="mb-4">
              <label htmlFor="reject-reason" className="mb-1 block text-sm text-gray-400">
                Reason (optional)
              </label>
              <textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Why is this listing being rejected?"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRejectingItem(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {rejectLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
